import { mergeAttributes } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import Mention from '@tiptap/extension-mention';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import type { ReportMentionItem } from './reportMentionTypes';
import { encodeMentionId } from './reportMentionTypes';
import { REPORT_LAYER_EMBED_NAME } from './createReportLayerEmbedExtension';
import {
  ReportMentionSuggestionList,
  type ReportMentionSuggestionListHandle,
  type ReportMentionSuggestionListProps,
} from './ReportMentionSuggestionList';

type CreateReportMentionExtensionArgs = {
  getItems: () => ReportMentionItem[];
  /**
   * When false, @ suggestions are disabled (click-to-view still works via
   * HTML).
   */
  suggestionsEnabled: boolean;
};

function filterMentionItems(
  items: ReportMentionItem[],
  query: string
): ReportMentionItem[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return items.slice(0, 40);
  return items
    .filter((item) => {
      const haystack =
        `${item.label} ${item.subtitle ?? ''} ${item.kind}`.toLowerCase();
      return haystack.includes(normalized);
    })
    .slice(0, 40);
}

function mentionDisplayText(node: { attrs: Record<string, unknown> }): string {
  const label = (node.attrs.label as string) || (node.attrs.id as string) || '';
  return `@${label}`;
}

export function createReportMentionExtension({
  getItems,
  suggestionsEnabled,
}: CreateReportMentionExtensionArgs) {
  // TipTap mention always maps `options.suggestion` in getSuggestions(); never pass
  // undefined or it crashes on `suggestion.char` (e.g. readOnly editors).
  const suggestion: Omit<SuggestionOptions<ReportMentionItem>, 'editor'> = {
    char: '@',
    allowSpaces: false,
    items: ({ query }) =>
      suggestionsEnabled ? filterMentionItems(getItems(), query) : [],
    allow: ({ state, range }) => {
      if (!suggestionsEnabled) return false;
      const $from = state.doc.resolve(range.from);
      const type = state.schema.nodes.mention;
      return !!$from.parent.type.contentMatch.matchType(type);
    },
    command: ({ editor, range, props }) => {
      const item = props as ReportMentionItem;
      const nodeAfter = editor.view.state.selection.$to.nodeAfter;
      const nextRange = { ...range };
      if (nodeAfter?.text?.startsWith(' ')) {
        nextRange.to += 1;
      }

      if (item.kind === 'layer') {
        editor
          .chain()
          .focus()
          .insertContentAt(nextRange, [
            {
              type: REPORT_LAYER_EMBED_NAME,
              attrs: {
                layerId: item.id,
                layerType: item.layerType || 'internal',
                label: item.label,
              },
            },
          ])
          .run();
        editor.view.dom.ownerDocument.defaultView
          ?.getSelection()
          ?.collapseToEnd();
        return;
      }

      const attrs = {
        id: encodeMentionId(item.kind, item.id, item.layerType),
        label: item.label,
        mentionKind: item.kind,
        mentionSuggestionChar: '@',
      };

      editor
        .chain()
        .focus()
        .insertContentAt(nextRange, [
          {
            type: 'mention',
            attrs,
          },
          { type: 'text', text: ' ' },
        ])
        .run();

      editor.view.dom.ownerDocument.defaultView
        ?.getSelection()
        ?.collapseToEnd();
    },
    render: () => {
      let component: ReactRenderer<
        ReportMentionSuggestionListHandle,
        ReportMentionSuggestionListProps
      > | null = null;

      return {
        onStart: (props: SuggestionProps<ReportMentionItem>) => {
          if (!suggestionsEnabled) return;
          component = new ReactRenderer(ReportMentionSuggestionList, {
            props: {
              items: props.items,
              command: props.command,
              clientRect: props.clientRect,
            },
            editor: props.editor,
          });
          document.body.appendChild(component.element);
        },
        onUpdate: (props: SuggestionProps<ReportMentionItem>) => {
          component?.updateProps({
            items: props.items,
            command: props.command,
            clientRect: props.clientRect,
          });
        },
        onKeyDown: (props: { event: KeyboardEvent }) => {
          if (props.event.key === 'Escape') {
            component?.destroy();
            component = null;
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },
        onExit: () => {
          component?.element.remove();
          component?.destroy();
          component = null;
        },
      };
    },
  };

  return Mention.extend({
    name: 'mention',
    addAttributes() {
      return {
        ...this.parent?.(),
        mentionKind: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-mention-kind'),
          renderHTML: (attributes) => {
            if (!attributes.mentionKind) return {};
            return { 'data-mention-kind': attributes.mentionKind };
          },
        },
      };
    },
  }).configure({
    HTMLAttributes: {
      class: 'report-mention',
      'data-type': 'mention',
    },
    suggestion,
    renderText({ node }) {
      return mentionDisplayText(node);
    },
    renderHTML({ options, node }) {
      return [
        'span',
        mergeAttributes({ 'data-type': 'mention' }, options.HTMLAttributes),
        mentionDisplayText(node),
      ];
    },
  });
}
